import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Json } from '../db_types'

type GeneratedPublicSchema = Database['public']
type GeneratedFunctions = GeneratedPublicSchema['Functions']

type FunctionWithArgs<
  FunctionName extends keyof GeneratedFunctions,
  Args extends Record<string, unknown>
> = Omit<GeneratedFunctions[FunctionName], 'Args'> & { Args: Args }

/**
 * RPC contracts that the generated database catalog cannot currently represent.
 *
 * Keep this limited to overloaded functions whose nullable arguments are collapsed
 * by the Supabase generator and functions added by migrations newer than the last
 * generated catalog. Regenerating db_types.ts remains the source of truth for the
 * rest of the schema.
 */
type RpcFunctionOverrides = {
  get_metric_preview_deal_custom_date_candidate_ids: {
    Args: {
      _organization_id: number
      _custom_field_id: number
      _profile_ids: string[]
      _date_start: string
      _date_end: string
      _limit: number
      _owner_custom_field_id?: number | null
    }
    Returns: { record_id: number }[]
  }
  get_team_memberships_in_period: FunctionWithArgs<
    'get_team_memberships_in_period',
    Omit<GeneratedFunctions['get_team_memberships_in_period']['Args'], '_team_ids'> & {
      _team_ids?: number[] | null
    }
  >
  set_target_credit_setup_selection: FunctionWithArgs<
    'set_target_credit_setup_selection',
    Omit<GeneratedFunctions['set_target_credit_setup_selection']['Args'], 'p_credit_setup_id'> & {
      p_credit_setup_id: number | null
    }
  >
  get_credited_target_achievement_series: {
    Args: {
      _target_id: number
      _profile_id: string
      _start_date: string
      _end_date: string
      _group_by: string
      _rule_segments: Json
    }
    Returns: GeneratedFunctions['get_period_target_achievement']['Returns']
  }
  get_credited_target_metric_values: {
    Args: { _requests: Json }
    Returns: GeneratedFunctions['get_credited_metric_values']['Returns']
  }
  get_credited_target_metric_value_aggregates: {
    Args: { _requests: Json }
    Returns: { request_id: number; value: number }[]
  }
  get_target_metric_source_records: {
    Args: {
      _target_id: number
      _metric_id: number
      _profile_id: string
      _period_start: string
      _period_end: string
      _organization_id: number
      _target_currency?: string
    }
    Returns: GeneratedFunctions['get_metric_source_records_with_inclusion_status']['Returns']
  }
  get_target_metric_source_records_page: {
    Args: {
      _target_id: number
      _metric_id: number
      _profile_id: string
      _period_start: string
      _period_end: string
      _organization_id: number
      _included: boolean
      _search?: string
      _offset: number
      _limit: number
      _target_currency?: string
    }
    Returns: Array<
      GeneratedFunctions['get_metric_source_records_with_inclusion_status']['Returns'][number] & {
        total_count: number
      }
    >
  }
  upsert_target_with_credit_setup_selection: {
    Args: {
      p_target: Database['public']['Tables']['targets']['Insert']
      p_credit_setup_id: number | null
    }
    Returns: Database['public']['Tables']['targets']['Row']
  }
}

type DolfinPublicSchema = Omit<GeneratedPublicSchema, 'Functions'> & {
  Functions: Omit<GeneratedFunctions, keyof RpcFunctionOverrides> & RpcFunctionOverrides
}

type DolfinDatabase = Omit<Database, 'public'> & { public: DolfinPublicSchema }

export type DolfinSupabaseClient = SupabaseClient<Database> & {
  rpc: SupabaseClient<DolfinDatabase>['rpc']
}

export const withDolfinRpcTypes = (client: SupabaseClient<Database>): DolfinSupabaseClient =>
  client as unknown as DolfinSupabaseClient
